/**
 * Bottom perk-legend cards for the Snake (Tram) game.
 *
 * Renders four labelled pills along the bottom edge of the screen — one per
 * perk type the player can pick up during a run (Slow Mo, Speed Up, Chaos,
 * Ghost). Modelled on `FroggerStartArea._buildCard()` so the visual style
 * is identical.
 *
 * Each card has three live states that {@link SnakeScene} drives:
 *   • idle      — shows title + one-line description.
 *   • flashing  — on perk pickup, the card briefly pulses (scale up + white
 *                 overlay fading out) to confirm which perk was collected.
 *   • counting  — while an effect with a duration is active (ghost / slow /
 *                 speed-trail), the description is hidden and a big bold
 *                 `Xs` countdown takes its place so the player can read the
 *                 remaining time without hunting in the top-left corner.
 *
 * Layout options on the constructor control how the row sits relative to
 * the bottom-centre D-pad:
 *   • `centerReserve`  — preferred. Splits the cards in half (2 left / 2
 *                        right) leaving a gap of `centerReserve` px in
 *                        the middle of the screen for the D-pad.
 *   • `leftReserve`    — legacy. Offsets the whole row to the right so
 *                        the D-pad can sit in the bottom-left corner.
 * `centerReserve` wins if both are provided.
 *
 * Card contents come from `i18n.js`:
 *   snake.perk_card_<id>_title    — short uppercase label
 *   snake.perk_card_<id>_desc     — one-line description (duration / effect)
 */

import { Container, Graphics, Text, TextStyle } from '../../../vendor/pixi.min.mjs';
import { t } from '../../config/i18n.js';
import { paintChip, darken } from '../../ui/shared.js';

const CARD_W = 240;
const CARD_H = 96;
const CARD_GAP = 24;
// Sharp-cornered, shadow-less footer cards — mirrors Frogger's
// `FroggerStartArea` so all four bottom-strip cards across both
// games share one flat-square aesthetic. The drop-shadow is also
// dropped at the call site below via `shadowAlpha: 0`.
const CARD_RADIUS = 0;
const ICON_SIZE = 56;
const CARDS_BOTTOM_PAD = 12;

/** How long the pickup flash animation lasts, in ms. */
const FLASH_DURATION_MS = 520;

const TITLE_STYLE = new TextStyle({
    fontFamily: 'Poppins, Arial', fontSize: 20, fontWeight: '800', fill: '#ffffff',
    dropShadow: { color: '#000000', distance: 1, blur: 3, alpha: 0.4 },
});

const DESC_STYLE = new TextStyle({
    fontFamily: 'Poppins, Arial', fontSize: 14, fontWeight: '600', fill: '#f1f5f9',
});

/**
 * Big bold countdown that replaces the description while the perk's
 * timed effect is running. Visually loud on purpose — this is the
 * replacement for the small top-left FX timer.
 */
const COUNTDOWN_STYLE = new TextStyle({
    fontFamily: 'Poppins, Arial',
    fontSize: 30,
    fontWeight: '900',
    fill: '#ffffff',
    stroke: { color: '#001a33', width: 3 },
    dropShadow: { color: '#000000', distance: 2, blur: 4, alpha: 0.65 },
});

/**
 * Cards shown along the bottom, left → right. `perkId` matches the
 * internal perk type id from `snakeConfig.PERK_TYPES` so {@link flash}
 * and {@link setCountdown} can be called with the same value
 * `SnakeScene._applyPerk` receives.
 *
 * Colours match the perk fallbacks defined in `data/snake.config.js → elements`.
 */
// Card body colours — pulled from the v2 UI mockup:
//   SLOW MO  — gold/amber (clock motif, matches the SCORE chip's gold)
//   SPEED UP — red (urgency)
//   CHAOS    — purple (matches the menu's chaos/disruption hue)
//   GHOST    — blue (phasing/ethereal)
// These are picked for visual contrast on the bottom strip; the on-board
// perk-pickup graphic still uses its own per-perk fallback from
// `data/snake.config.js → elements`.
const PERKS = [
    { id: 'slow',  perkId: 'slow',        iconSlot: 'snake.perk.slow',        color: 0xfbbf24 },
    { id: 'speed', perkId: 'speed_up',    iconSlot: 'snake.perk.speed_up',    color: 0xef4444 },
    { id: 'chaos', perkId: 'random_turn', iconSlot: 'snake.perk.random_turn', color: 0xa855f7 },
    { id: 'ghost', perkId: 'ghost',       iconSlot: 'snake.perk.ghost',       color: 0x3b82f6 },
];

export class SnakeBottomCards {
    container = new Container();
    _game = null;
    /**
     * @type {Array<{
     *     perkId: string,
     *     root: import('../../../vendor/pixi.min.mjs').Container,
     *     desc: import('../../../vendor/pixi.min.mjs').Text,
     *     countdown: import('../../../vendor/pixi.min.mjs').Text,
     *     flashOverlay: import('../../../vendor/pixi.min.mjs').Graphics,
     *     flashUntil: number,
     * }>}
     */
    _cards = [];

    /**
     * @param {*} game
     * @param {{ leftReserve?: number, rightReserve?: number, centerReserve?: number }} [opts]
     *   centerReserve — px to leave free in the MIDDLE of the screen
     *                   (clears a centre-anchored D-pad). Cards are
     *                   split 2-left / 2-right around the gap.
     *   leftReserve   — px to leave free on the left (legacy bottom-left
     *                   D-pad layout). Ignored when `centerReserve` is set.
     *   rightReserve  — px to leave free on the right (legacy).
     */
    constructor(game, opts = {}) {
        this._game = game;
        this._centerReserve = opts.centerReserve ?? 0;
        this._leftReserve = opts.leftReserve ?? 0;
        this._rightReserve = opts.rightReserve ?? 0;
        this._build();
    }

    static get HEIGHT() { return CARD_H + CARDS_BOTTOM_PAD; }

    _build() {
        const screenW = this._game.width;
        const screenH = this._game.height;
        const cardsY = screenH - CARD_H - CARDS_BOTTOM_PAD;

        // Centre-reserve mode: split the perks into two equal halves and
        // place each half flush against its side of the centre gap, so
        // the D-pad slot in the middle stays clear regardless of how
        // many cards there are. With the current 4 perks we get 2 on
        // each side, exactly as the UX direction asks for.
        if (this._centerReserve > 0) {
            const half = Math.ceil(PERKS.length / 2);
            const leftHalf  = PERKS.slice(0, half);
            const rightHalf = PERKS.slice(half);
            const screenCx  = screenW / 2;
            const halfGap   = this._centerReserve / 2;

            const leftHalfW = leftHalf.length * CARD_W
                + Math.max(0, leftHalf.length - 1) * CARD_GAP;
            const rightHalfW = rightHalf.length * CARD_W
                + Math.max(0, rightHalf.length - 1) * CARD_GAP;

            const leftStartX  = screenCx - halfGap - leftHalfW;
            const rightStartX = screenCx + halfGap;

            // Build the left half in source order so card 0 stays the
            // visually leftmost — preserves existing pickup-flash mapping.
            for (let i = 0; i < leftHalf.length; i++) {
                this._addCard(leftHalf[i], leftStartX + i * (CARD_W + CARD_GAP), cardsY);
            }
            for (let i = 0; i < rightHalf.length; i++) {
                this._addCard(rightHalf[i], rightStartX + i * (CARD_W + CARD_GAP), cardsY);
            }
            return;
        }

        // Legacy single-row layout (used by callers that don't pass
        // centerReserve). Kept so any future Snake mode that wants the
        // old bottom-left pad / right-aligned cards arrangement still works.
        const availW = screenW - this._leftReserve - this._rightReserve;
        const totalCardsW = PERKS.length * CARD_W + (PERKS.length - 1) * CARD_GAP;
        const startX = this._leftReserve + Math.max(0, (availW - totalCardsW) / 2);
        for (let i = 0; i < PERKS.length; i++) {
            this._addCard(PERKS[i], startX + i * (CARD_W + CARD_GAP), cardsY);
        }
    }

    /**
     * Construct + position one perk card. Pivots the root at the card's
     * centre so {@link flash} can scale the card from its middle
     * (instead of from the top-left corner) without visually shifting it.
     */
    _addCard(def, leftX, topY) {
        const entry = this._buildCard(def);
        entry.root.pivot.set(CARD_W / 2, CARD_H / 2);
        entry.root.x = leftX + CARD_W / 2;
        entry.root.y = topY + CARD_H / 2;
        this.container.addChild(entry.root);
        this._cards.push(entry);
    }

    _buildCard(def) {
        const root = new Container();

        // v2 bevel stack — keeps the dark rim + bevel highlight + faded
        // white inner stroke that gives the card depth, but the drop
        // shadow is dropped (`shadowAlpha: 0`) per UX direction so the
        // strip reads as a flat panel pinned to the bottom of the play
        // field. Mirrors `FroggerStartArea` so the two games' bottom
        // strips share one shadow-less, square-cornered look.
        const face = paintChip({
            w: CARD_W,
            h: CARD_H,
            r: CARD_RADIUS,
            color: def.color,
            shadowAlpha: 0,
        });
        root.addChild(face);

        // White medallion behind the perk icon, ringed in a darker tone
        // of the card colour. Larger than on the top chips because the
        // bottom strip has more vertical room and the v2 mockup leans
        // into a "stamped icon" look for these cards.
        const rim = darken(def.color, 0.55);
        const iconBg = new Graphics();
        iconBg.circle(40, CARD_H / 2, ICON_SIZE / 2 + 6);
        iconBg.fill({ color: 0xffffff, alpha: 0.95 });
        iconBg.stroke({ color: rim, width: 3 });
        root.addChild(iconBg);

        const icon = this._game.theme?.createSprite(def.iconSlot, ICON_SIZE, ICON_SIZE, {
            anchorX: 0.5, anchorY: 0.5, fitMode: 'contain',
        });
        if (icon) {
            icon.x = 40;
            icon.y = CARD_H / 2;
            root.addChild(icon);
        }

        const title = new Text({ text: t(`snake.perk_card_${def.id}_title`), style: TITLE_STYLE });
        title.x = 80;
        title.y = 20;
        root.addChild(title);

        const desc = new Text({ text: t(`snake.perk_card_${def.id}_desc`), style: DESC_STYLE });
        desc.x = 80;
        desc.y = 54;
        root.addChild(desc);

        // Big countdown text drops in over the description while an
        // effect is running. Anchored right where `desc` sits so the
        // visual swap doesn't shift the card layout.
        const countdown = new Text({ text: '', style: COUNTDOWN_STYLE });
        countdown.x = 80;
        countdown.y = 44;
        countdown.visible = false;
        root.addChild(countdown);

        // White flash overlay used by {@link flash}. Stays at alpha 0
        // and is decayed by {@link update} each frame after a pickup.
        const flashOverlay = new Graphics();
        flashOverlay.roundRect(0, 0, CARD_W, CARD_H, CARD_RADIUS);
        flashOverlay.fill({ color: 0xffffff, alpha: 1 });
        flashOverlay.alpha = 0;
        root.addChild(flashOverlay);

        return {
            perkId: def.perkId,
            root,
            desc,
            countdown,
            flashOverlay,
            flashUntil: 0,
        };
    }

    /**
     * Trigger the pickup-confirmation pulse on the card matching `perkId`.
     * Silent no-op if `perkId` doesn't map to any card (e.g. `shrink` —
     * it doesn't live on the bottom strip).
     *
     * @param {string} perkId one of snakeConfig.PERK_TYPES' `id` values
     */
    flash(perkId) {
        const card = this._cards.find((c) => c.perkId === perkId);
        if (!card) return;
        card.flashUntil = Date.now() + FLASH_DURATION_MS;
    }

    /**
     * Set / clear the on-card countdown for a timed effect. Pass
     * `seconds <= 0` to hide the countdown and restore the description.
     *
     * @param {string} perkId one of snakeConfig.PERK_TYPES' `id` values
     * @param {number} seconds whole seconds remaining (already ceil'd)
     */
    setCountdown(perkId, seconds) {
        const card = this._cards.find((c) => c.perkId === perkId);
        if (!card) return;
        if (seconds <= 0) {
            if (card.countdown.visible) {
                card.countdown.visible = false;
                card.desc.visible = true;
            }
            return;
        }
        const text = `${seconds}s`;
        if (card.countdown.text !== text) card.countdown.text = text;
        if (!card.countdown.visible) {
            card.countdown.visible = true;
            card.desc.visible = false;
        }
    }

    /**
     * Per-frame: advance the flash animation on any cards currently
     * mid-pulse. Cheap — early-outs when no card is flashing.
     */
    update() {
        const now = Date.now();
        for (const card of this._cards) {
            if (card.flashUntil > now) {
                const t01 = 1 - (card.flashUntil - now) / FLASH_DURATION_MS;
                // Punchy scale that peaks mid-animation and returns to 1.
                const scale = 1 + 0.14 * Math.sin(t01 * Math.PI);
                card.root.scale.set(scale);
                // White overlay flashes bright at t=0 and decays out.
                card.flashOverlay.alpha = (1 - t01) * 0.85;
            } else if (card.root.scale.x !== 1) {
                card.root.scale.set(1);
                card.flashOverlay.alpha = 0;
            }
        }
    }

    setVisible(v) {
        this.container.visible = !!v;
    }

    destroy() {
        this.container.removeChildren();
        if (this.container.parent) this.container.parent.removeChild(this.container);
        this._cards = [];
        this._game = null;
    }
}
